import { APP_INITIALIZER, CUSTOM_ELEMENTS_SCHEMA, ErrorHandler, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';

import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';

import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS } from '@angular/material-moment-adapter';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import {
  MsalInterceptor,
  MsalModule
} from '@azure/msal-angular';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { MissingTranslationHandler, TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { environment } from '../environments/environment';
import { DashboardModule } from './dashboard/dashboard.module';
import { EnvServiceProvider } from './env.service.provider';
import { ErrorPageModule } from './error-page/error-page.module';
import { HelpModule } from './help/help.module';
import { MaterialModule } from './material.module';
import { NavHeaderModule } from './nav-header/nav-header.module';
import { metaReducers, reducers } from './reducers';
import { RequestRefDataEffects } from './reducers/ref-data/refdata.effects';
import { SubstituteEffects } from './reducers/substitute/substitute.effects';
import { UserEffects } from './reducers/user/user.effects';
import { MissingTranslationService } from './services/missing-translation.service';
import MomentSettingsInitializer from './services/moment-settings.initializer';
import { msalFactory, msalGuardConfig, msalInterceptorConfig } from './shared/constants/msal.constants';
import { EncodeHttpParamsInterceptor } from './shared/interceptors/encode-http-params.interceptor';
import { UsernameHeaderInterceptor } from './shared/interceptors/username-header.interceptor';
import { ApplicationInsightsErrorHandler } from './shared/services/application-insights-error-handler';
import { PitMsalService } from './shared/services/pit-msal.service';
import { PitTranslateLoader } from './shared/services/pit-translate-loader.service';
import { SharedModule } from './shared/shared.module';
import { SubstituteModule } from './substitute/substitute.module';
import { VersionModule } from './version/version.module';

const disableAnimations =
  !('animate' in document.documentElement)
  || (navigator && /iPhone OS (8|9|10|11|12|13)_/.test(navigator.userAgent));


@NgModule({
  declarations: [
    AppComponent,
  ],
  imports: [
    HttpClientModule,
    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule.withConfig({ disableAnimations }),
    SharedModule,
    MaterialModule,
    NavHeaderModule,
    DashboardModule,
    HelpModule,
    VersionModule,
    ErrorPageModule,
    MsalModule.forRoot(msalFactory(), msalGuardConfig, msalInterceptorConfig),
    StoreModule.forRoot(reducers, { metaReducers }),
    SubstituteModule,
    EffectsModule.forRoot([
      RequestRefDataEffects,
      SubstituteEffects,
      UserEffects,
    ]),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useExisting: PitTranslateLoader,
      },
      missingTranslationHandler: {provide: MissingTranslationHandler, useClass: MissingTranslationService},
      defaultLanguage: 'en'
    }),
    !environment.production ? StoreDevtoolsModule.instrument() : []
  ],
  providers: [
    EnvServiceProvider,
    PitMsalService,
    {
      provide: ErrorHandler,
      useClass: ApplicationInsightsErrorHandler
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: UsernameHeaderInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: EncodeHttpParamsInterceptor,
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => MomentSettingsInitializer,
      multi: true
    },
    { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { useUtc: false } },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: {
        hideRequiredMarker: true
      }
    }
  ], // send all dates in EST
  schemas: [ CUSTOM_ELEMENTS_SCHEMA ],
  bootstrap: [ AppComponent ]
})
export class AppModule {
  constructor() {
    MomentSettingsInitializer();
  }
}
